import axios from 'axios';
import { isUserInList } from './auth';
import {
  ensureUserStorageHydrated,
  migrateStorageKeysToCurrentUser,
} from './userStorage';

const AUTH_STORAGE_KEYS = [
  'favorites',
  'recentItemIds',
  'homeColumns',
  'passwords',
  'currentTheme',
  'pinnedItemIds',
  'hiddenItemIds',
];

export const syncAuthSession = async user => {
  if (!user?._id) return;
  window.localStorage.setItem('auth_user', JSON.stringify(user));
  migrateStorageKeysToCurrentUser(AUTH_STORAGE_KEYS);
  await ensureUserStorageHydrated();
};

export const loadUserRoles = async userId => {
  const resolveUniverseRole = universe => {
    const ownerId = universe?.owner?.toString?.() ?? universe?.owner;
    if (ownerId && ownerId === userId) {
      return 'owner';
    }
    if (isUserInList(universe?.access?.admins, userId)) {
      return 'admin';
    }
    return null;
  };

  const resolveCampaignRole = campaign => {
    const ownerId = campaign?.owner?.toString?.() ?? campaign?.owner;
    if (ownerId && ownerId === userId) {
      return 'owner';
    }
    if (isUserInList(campaign?.access?.admins, userId)) {
      return 'admin';
    }
    if (isUserInList(campaign?.access?.editors, userId)) {
      return 'editor';
    }
    if (isUserInList(campaign?.access?.readers, userId)) {
      return 'reader';
    }
    return null;
  };

  const universesRes = await axios.get(
    `${process.env.REACT_APP_API_URL}/items/universes`,
  );
  const universes = Array.isArray(universesRes?.data) ? universesRes.data : [];

  const roleUniverses = universes
    .map(universe => {
      const role = resolveUniverseRole(universe);
      if (!role) return null;
      return {
        _id: universe._id,
        label: universe?.label || universe?._id,
        role,
        item: universe,
        ownerId: universe?.owner?.toString?.() ?? universe?.owner ?? null,
      };
    })
    .filter(Boolean);
  const universeIdsWithFullAccess = new Set(
    roleUniverses
      .filter(entry => entry.role === 'owner' || entry.role === 'admin')
      .map(entry => entry._id),
  );

  const campaignResponses = await Promise.all(
    universes.map(universe =>
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/items/allcampaigns/${universe?._id}`,
        )
        .then(res => ({
          universeId: universe?._id,
          campaigns: Array.isArray(res?.data) ? res.data : [],
        }))
        .catch(() => ({
          universeId: universe?._id,
          campaigns: [],
        })),
    ),
  );

  const roleCampaigns = [];
  campaignResponses.forEach(({ universeId, campaigns }) => {
    campaigns.forEach(campaign => {
      if (universeIdsWithFullAccess.has(universeId)) return;
      const role = resolveCampaignRole(campaign);
      if (!role) return;
      roleCampaigns.push({
        _id: campaign._id,
        label: campaign?.label || campaign?._id,
        role,
        universeId,
        item: campaign,
        ownerId: campaign?.owner?.toString?.() ?? campaign?.owner ?? null,
      });
    });
  });

  const ownerIds = Array.from(
    new Set(
      [...roleUniverses, ...roleCampaigns]
        .map(entry => entry?.ownerId)
        .filter(Boolean),
    ),
  );

  let ownerUsers = {};
  if (ownerIds.length > 0) {
    try {
      const usersRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/lookup`,
        { ids: ownerIds },
      );
      const users = Array.isArray(usersRes?.data?.users)
        ? usersRes.data.users
        : [];
      ownerUsers = users.reduce((acc, user) => {
        if (user?._id) {
          acc[user._id] = user;
        }
        return acc;
      }, {});
    } catch (e) {
      ownerUsers = {};
    }
  }

  return { roleUniverses, roleCampaigns, ownerUsers };
};
